import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
  Step,
  Warning,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const Defects = () => (
  <Page
    title="Defects"
    intro="Defects can be logged against a vehicle or an item of equipment by anyone, then prioritised, assigned, and tracked through to completion by the fleet team."
  >
    <Section title="Reporting a defect">
      <Step number={1} title="Open the form">
        Go to <DocLink url="/report-defect" text="Report Defect" />. It is
        available to every signed-in user — not just fleet teams — so
        operational issues can be raised the moment they are spotted.
      </Step>
      <Step number={2} title="Say what it affects">
        Tick whether it is a vehicle defect and pick the vehicle, or enter
        the asset tag for equipment — typing looks the real asset ID up as
        you go, or scan the item's QR code to pre-fill it.
      </Step>
      <Step number={3} title="Describe the problem">
        Add a summary and description, flag it as an infection prevention
        and control (IPC) issue if relevant, and attach photos. For a vehicle
        or tagged equipment you are also asked whether it is still fit for
        use — a vehicle reported as not fit for use is marked Out of Service
        automatically, unless your organisation has switched that off in
        Site Configuration → Fleet → Defect Alerts.
      </Step>
      <Info title="Defects from a VDI">
        A defect can also be raised directly from a Vehicle Daily Inspection
        when crew spot an issue during their daily check — see{' '}
        <DocLink
          url="/fleet-equipment/vdis-and-safety-checks"
          text="VDIs and Safety Checks"
          docsPage
        />
        .
      </Info>
    </Section>

    <Section title="Tracking defects">
      <Bullets>
        <Bullet>
          Open <DocLink url="/defects" text="Defects" /> for a Kanban-style
          board of defects, filterable by status:{' '}
          <strong>New</strong>, <strong>In Progress</strong>,{' '}
          <strong>Waiting</strong>, <strong>Completed</strong>, and{' '}
          <strong>Duplicate - Closed</strong> (for a repeat report of a
          defect already logged — it counts as completed).
        </Bullet>
        <Bullet>
          On an individual defect, set the status and the priority (Low,
          Normal, or High), assign it to an employee, and review the
          affected vehicle or asset, photos, and IPC flag.
        </Bullet>
        <Bullet>
          Defects also appear on the affected vehicle's record and asset
          record, and open defect counts show on the vehicle list.
        </Bullet>
      </Bullets>
      <Screenshot url="fleet-equipment/defects-board.png">
        The defects board with defects grouped by status from New through to
        Completed.
      </Screenshot>
      <Warning title="Who can do this?">
        Reporting a defect and updating one are open to any signed-in user.
        The defects board and individual defect records require the{' '}
        <strong>Fleet</strong>, <strong>Make Ready</strong>, or{' '}
        <strong>Governance</strong> team (site administrators can always do
        everything).
      </Warning>
    </Section>

    <Section title="Defect alerts">
      <Bullets>
        <Bullet>
          When a defect is reported, AmbuNet alerts your{' '}
          <strong>designated defect contacts</strong> — set up in Site
          Configuration → Fleet → Defect Alerts from anyone who can open a
          defect (the Fleet, Make Ready, or Governance team, or a site
          admin). A contact can be limited to particular bases, in which
          case they are only told about vehicles at those bases — equipment
          defects have no base, so they always go to everyone listed.
        </Bullet>
        <Bullet>
          A separate switch controls whether the whole{' '}
          <strong>Fleet and Make Ready teams</strong> are alerted as well.
          It is on by default; switch it off once you have designated
          contacts if only they should hear about new defects.
        </Bullet>
        <Bullet>
          Alerts arrive through the notification bell, email, and mobile
          push (subject to each person's own notification preferences), and
          link straight to the defect. Whoever reported the defect is never
          alerted about their own report.
        </Bullet>
      </Bullets>
      <Info title="Who can do this?">
        Viewing and changing the defect alert settings requires the{' '}
        <strong>Fleet</strong> team (site administrators always can).
      </Info>
    </Section>
  </Page>
)

export default Defects
